import { useEffect, useState } from "react";
import EmployeeTable from "uicomponents/EmployeeTable";
import styles from "./CobraCensus.module.css";
import { COBRA_CENSUS_CONTENTS } from "../../utils/constants";
import { useCensusAndCurrentInformationContext } from "pages/CensusAndCurrentInformation/CensusAndCurrentInformation";
import { cobraCensusInitialValue } from "pages/CensusAndCurrentInformation/utils/initialFormValues";
import { IEmployeeCensusFormRow } from "uicomponents/EmployeeTable/EmployeeTable";
import {
  trackFormError,
  trackPageViewUserStep,
  trackFormSubmission,
} from "utils/analytics/trackEvent";
import { ConvertToISODate, errorMessageMap } from "utils/helper";
import Alert from "uicomponents/Alert";
import { CobraCensusType } from "types";
import { getProspectUuid } from "utils/idHelpers";
import {
  useDeleteCobraParticipantMutation,
  useUpsertCobraParticipantsMutation,
  useUpdateStepMutation,
} from "types/generated/operations";
import {
  GenderTypeEnum,
  ParticipantTypeEnum,
  PlanTierEnum,
} from "types/generated/schema";
import { currentEmployeesValidationSchema } from "pages/CensusAndCurrentInformation/utils/validationSchema";
import { mapBEStepstoMatchFE } from "pages/CensusAndCurrentInformation/utils/helper";
import newstyles from "../../CensusAndCurrentInformation.module.css";
import {
  STEPS_WITH_COBRA,
  STEPS_WITHOUT_COBRA,
} from "pages/CensusAndCurrentInformation/utils/constants";
import Stepper from "uicomponents/Stepper";
import { getStepNumberFromURL } from "utils/helper";
import Error from "uicomponents/Error";

interface ICobraCensusProps {
  data: CobraCensusType;
}

const CobraCensus = (props: ICobraCensusProps): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageViewUserStep(5, "current empoloyee information");
  }, []);

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const { data } = props;

  const hasCobra = data?.healthInsuranceInformation?.cobraUsage;
  const STEPS = hasCobra ? STEPS_WITH_COBRA : STEPS_WITHOUT_COBRA;

  const { previousStep, nextStep, stepperNextStep } =
    useCensusAndCurrentInformationContext();

  const initialFormValues = cobraCensusInitialValue(data?.cobraCensuses);

  const [upsertCobraParticipants, { loading: upsertCobraParticipantsLoading }] =
    useUpsertCobraParticipantsMutation();
  const [deleteCobraParticipant, { loading: deleteCobraParticipantLoading }] =
    useDeleteCobraParticipantMutation();
  const [updateStep, { loading: updateStepLoading }] = useUpdateStepMutation();

  const applicationStep = data?.applications?.[0]?.step;
  const currentStep = getStepNumberFromURL();
  const isInValidStep = applicationStep && currentStep > applicationStep;

  if (isInValidStep) {
    return <Error />;
  }

  const handleSubmitForm = async (values: IEmployeeCensusFormRow) => {
    const filteredValues = values.employees?.filter(
      (emp) => !!emp.firstName && !!emp.zip
    );
    try {
      const currentStep = getStepNumberFromURL();
      if (!filteredValues) {
        return;
      }
      await upsertCobraParticipants({
        variables: {
          prospectId: getProspectUuid(),
          cobraCensuses: filteredValues?.map((eachEmp) => {
            return {
              uuid: eachEmp?.uuid || undefined,
              firstName: eachEmp.firstName || "",
              lastName: eachEmp.lastName || "",
              gender: eachEmp.gender
                ?.toUpperCase()
                .replace(/-/g, "_") as GenderTypeEnum,
              participantType: eachEmp.participantType
                ?.toUpperCase()
                .replace(/-/g, "_") as ParticipantTypeEnum,
              dateOfBirth: ConvertToISODate(eachEmp.dateOfBirth || ""),
              zip: eachEmp.zip,
              healthInsInfo: {
                currentPlanName: eachEmp.currentPlanName || "",
                currentEnrollmentTier: eachEmp.currentEnrollTier
                  ? (eachEmp.currentEnrollTier
                      ?.toUpperCase()
                      .replace(/-/g, "_") as PlanTierEnum)
                  : undefined,
                currentMonthlyPremium:
                  Number(eachEmp.currentMonthlyPremium) || undefined,
                monthlyPremiumPostRenewal:
                  Number(eachEmp.monthlyPremiumPostRenewal) || undefined,
              },
            };
          }),
        },
      });
      if (data?.applications?.[0]?.step === currentStep) {
        await updateStep({
          variables: {
            uuid: data?.applications?.[0].uuid,
            step: data?.applications?.[0]?.step + 1,
          },
        });
        trackFormSubmission(7, "CensusAndCurrentInfo");
      }
      nextStep();
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, errorMessage);
      setIsError(true);
      return;
    }
  };

  const handleDelete = async (uuid: string) => {
    try {
      if (!uuid) {
        return true;
      }
      await deleteCobraParticipant({
        variables: {
          uuid,
        },
      });

      return true;
    } catch (error: any) {
      const errorMessage: string = error?.message;
      setErrorMessage(errorMessageMap[errorMessage] || "Please try again");
      trackFormError(error, errorMessage);
      setIsError(true);
      return false;
    }
  };

  return (
    <div className={newstyles.censusAndCurrentInformationformContainer}>
      <Stepper
        steps={STEPS}
        currentStep={mapBEStepstoMatchFE(getStepNumberFromURL())}
        status={mapBEStepstoMatchFE(data?.applications?.[0].step || 0) || 8}
        onClick={(stepNumber) => stepperNextStep(stepNumber)}
      />
      <div className={newstyles.censusAndCurrentInformationformDiv}>
        <div className={styles.pageTitle}>
          {COBRA_CENSUS_CONTENTS.pageTitle}
        </div>

        <div className={styles.introduction}>
          {COBRA_CENSUS_CONTENTS.introduction}
        </div>

        <EmployeeTable
          formikProps={{
            validationSchema: currentEmployeesValidationSchema,
            initialValues: initialFormValues,
            onSubmit: handleSubmitForm,
          }}
          handleBackClick={previousStep}
          handleDelete={handleDelete}
          offerHealthInsurance={
            !!props.data?.healthInsuranceInformation
              ?.currentlyOfferHealthInsurance
          }
          isLoading={
            upsertCobraParticipantsLoading ||
            deleteCobraParticipantLoading ||
            updateStepLoading
          }
          isCobra
        />

        {isError && <Alert message={errorMessage} />}
      </div>
    </div>
  );
};

export default CobraCensus;
